import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "bpr_cookie_consent_v1";

type Preferences = {
  essential: true;
  analytics: boolean;
  functionality: boolean;
  advertising: boolean;
  decidedAt: string;
};

const loadConsent = (): Preferences | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Preferences;
  } catch {
    return null;
  }
};

const saveConsent = (prefs: Preferences) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new CustomEvent("bpr-cookie-consent", { detail: prefs }));
  } catch {}
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [functionality, setFunctionality] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  useEffect(() => {
    if (loadConsent()) return;
    // Wait for web fonts to load before showing the banner to prevent
    // a layout shift caused by font swap once the banner is on screen.
    const show = () => setVisible(true);
    const fontsReady =
      typeof document !== "undefined" && (document as any).fonts?.ready;
    if (fontsReady && typeof fontsReady.then === "function") {
      let done = false;
      fontsReady.then(() => {
        if (done) return;
        done = true;
        show();
      });
      // Safety fallback in case fonts.ready never resolves.
      const t = setTimeout(() => {
        if (done) return;
        done = true;
        show();
      }, 3000);
      return () => clearTimeout(t);
    }
    show();
  }, []);

  const finalize = (prefs: Omit<Preferences, "essential" | "decidedAt">) => {
    saveConsent({
      essential: true,
      decidedAt: new Date().toISOString(),
      ...prefs,
    });
    setVisible(false);
    setManageOpen(false);
  };

  const acceptAll = () =>
    finalize({ analytics: true, functionality: true, advertising: true });
  const rejectNonEssential = () =>
    finalize({ analytics: false, functionality: false, advertising: false });
  const savePreferences = () =>
    finalize({ analytics, functionality, advertising });

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pointer-events-none"
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
      >
        <div className="pointer-events-auto mx-auto max-w-4xl rounded-lg border bg-card shadow-lg p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-6 w-6 text-primary shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground leading-relaxed">
                <p className="font-medium text-foreground mb-1">We use cookies</p>
                <p>
                  We use essential cookies to make our site work, and optional
                  cookies for analytics, functionality, and advertising. See our{" "}
                  <Link to="/cookies" className="text-primary hover:underline">
                    Cookie Policy
                  </Link>{" "}
                  for details.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setManageOpen(true)}
              >
                Manage Preferences
              </Button>
              <Button variant="outline" size="sm" onClick={rejectNonEssential}>
                Reject Non-Essential
              </Button>
              <Button size="sm" onClick={acceptAll}>
                Accept All
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cookie Preferences</DialogTitle>
            <DialogDescription>
              Choose which optional cookies to allow. Essential cookies are always
              on so the site can function.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground text-sm">
                  Essential
                </p>
                <p className="text-xs text-muted-foreground">
                  Required for login, security, and basic navigation.
                </p>
              </div>
              <Switch checked disabled aria-label="Essential cookies (always on)" />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground text-sm">
                  Performance & Analytics
                </p>
                <p className="text-xs text-muted-foreground">
                  Help us understand how visitors use the site.
                </p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label="Analytics cookies"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground text-sm">Functionality</p>
                <p className="text-xs text-muted-foreground">
                  Remember preferences such as saved settings.
                </p>
              </div>
              <Switch
                checked={functionality}
                onCheckedChange={setFunctionality}
                aria-label="Functionality cookies"
              />
            </div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground text-sm">
                  Advertising & Marketing
                </p>
                <p className="text-xs text-muted-foreground">
                  Used to show relevant ads and measure performance.
                </p>
              </div>
              <Switch
                checked={advertising}
                onCheckedChange={setAdvertising}
                aria-label="Advertising cookies"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={rejectNonEssential}>
              Reject Non-Essential
            </Button>
            <Button onClick={savePreferences}>Save Preferences</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsentBanner;
